import type { KeyboardEvent, ReactNode } from "react";
import { NinjaArt } from "../art/NinjaArt";
import type { Mode } from "../content/modes";
import { RANKS } from "../content/ranks";
import { variantName } from "../content/variants";
import type { Frame } from "../game/sets";
import { SetDots } from "./SetDots";
import { Stripes } from "./Stripes";
import "./RankCard.css";

interface Props {
  rankIndex: number;
  /** Which strand's look to show. Classic (Add) when left out. */
  variant?: Mode | undefined;
  /** Stripes earned towards the next rank; the notches show when this is set. */
  stripes?: number | undefined;
  frame?: Frame | undefined;
  /** Small label above the card, e.g. "Ninja rank". */
  kicker?: string | undefined;
  /** Extra line under the motto, e.g. the best score. */
  bestLine?: string | undefined;
  /** The strands that hold this rank, towards gold. Shown when given. */
  dots?: { mode: Mode; held: boolean }[] | undefined;
  onOpen?: ((rankIndex: number) => void) | undefined;
  className?: string | undefined;
  children?: ReactNode;
}

/** The big rank card shown on Home and Result. Tapping it opens the detail card. */
export function RankCard({
  rankIndex,
  variant = "add",
  stripes,
  frame = null,
  kicker,
  bestLine,
  dots,
  onOpen,
  className,
  children,
}: Props) {
  const rank = RANKS[rankIndex] ?? RANKS[0]!;
  const name = variantName(variant, rank.name);
  const interactive = !!onOpen;
  const activate = () => onOpen?.(rankIndex);
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      activate();
    }
  };
  return (
    <div
      className={["card", frame ? `frame-${frame}` : "", className].filter(Boolean).join(" ")}
      style={{ "--c": rank.colour } as React.CSSProperties}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={
        interactive
          ? `See the ${frame ? `${frame} ` : ""}${name} card` +
            (stripes ? `, ${stripes} of 3 stripes` : "")
          : undefined
      }
      onClick={interactive ? activate : undefined}
      onKeyDown={interactive ? onKeyDown : undefined}
    >
      {(kicker || frame) && (
        <div className="card-kicker">
          {kicker && <span>{kicker}</span>}
          {frame && (
            <span className={"crest" + (frame === "foil" ? " foil" : "")}>
              {frame === "foil" ? "Foil" : "Gold"}
            </span>
          )}
        </div>
      )}
      <NinjaArt rank={rank} variant={variant} stripes={stripes} className="card-art" />
      <div className="card-rank display">
        {name}
        {stripes !== undefined && <Stripes count={stripes} className="card-stripes" />}
      </div>
      <p className="card-motto">{rank.motto}</p>
      {dots && <SetDots dots={dots} className="card-dots" />}
      {bestLine && <p className="card-best">{bestLine}</p>}
      {children}
    </div>
  );
}
